import * as React from "react";
import { useEffect, useCallback, useRef, useState } from "react";
import { SearchBox, SearchBoxChangeEvent, InputOnChangeData, makeStyles, tokens } from "@fluentui/react-components";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { Project } from "../../models/ProjectsResponse";
import { IProjectData } from "../../models/shared/IProjectData";
import { useFilteredProjects } from "../../hooks/useFilteredProjects";

export interface SendAndFileProjectSearchProps {
    projectsService: IProjectsService;
    myProjects: Project[];
    isLoadingMyProjects: boolean;
    showAllGlobalProjects: boolean;
    filterString: string;
    onSearchChange: (value: string) => void;
    onSearchClear: () => void;
    onSearchResults: (results: IProjectData[], isSearching: boolean) => void;
    translate: (key: string) => string;
    theme: string;
    disabled?: boolean;
}

const useStyles = makeStyles({
    root: {
        width: "100%",
        "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
});

const SendAndFileProjectSearch: React.FC<SendAndFileProjectSearchProps> = (props) => {
    const styles = useStyles();
    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";
    const { projectsService, showAllGlobalProjects, onSearchResults } = props;

    // Local input state to avoid re-render interference from parent
    const [localValue, setLocalValue] = useState(props.filterString);

    // Sync from parent only when parent clears (e.g. on refresh)
    useEffect(() => {
        if (props.filterString === "" && localValue !== "") {
            setLocalValue("");
        }
    }, [props.filterString]);

    const { searchResults, isSearching, setQuery } = useFilteredProjects({
        projectsService,
        autoPrime: showAllGlobalProjects,
    });

    // Forward query changes to hook only when global projects are enabled
    useEffect(() => {
        if (showAllGlobalProjects) {
            setQuery(localValue);
        }
    }, [localValue, setQuery, showAllGlobalProjects]);

    // Forward results to parent — use ref to avoid loops
    const prevResultsRef = useRef<IProjectData[]>([]);
    const prevIsSearchingRef = useRef(false);
    useEffect(() => {
        if (prevResultsRef.current !== searchResults || prevIsSearchingRef.current !== isSearching) {
            prevResultsRef.current = searchResults;
            prevIsSearchingRef.current = isSearching;
            onSearchResults(searchResults, isSearching);
        }
    }, [searchResults, isSearching, onSearchResults]);

    const placeholderText = props.isLoadingMyProjects
        ? props.translate("LOADING_PROJECTS")
        : props.showAllGlobalProjects
        ? props.translate("SEARCH_PROJECTS")
        : props.translate("SEARCH_MYPROJECTS");

    const handleChange = useCallback(
        (_ev: SearchBoxChangeEvent, data: InputOnChangeData) => {
            const value = data.value ?? "";
            setLocalValue(value);
            props.onSearchChange(value);
        },
        [props.onSearchChange]
    );

    const handleDismiss = useCallback(() => {
        setLocalValue("");
        props.onSearchClear();
    }, [props.onSearchClear]);

    return (
        <SearchBox
            data-testid="send-and-file-project-search"
            value={localValue}
            placeholder={placeholderText}
            onChange={handleChange}
            dismiss={{ onClick: handleDismiss }}
            disabled={props.disabled || props.isLoadingMyProjects}
            className={styles.root}
            style={{ backgroundColor: isDarkMode ? tokens.colorNeutralBackground3 : "" }}
        />
    );
};

export default SendAndFileProjectSearch;
